import type { ReactNode } from 'react';
import { Pill } from './Pill';
import type { ProjectReviewProgress } from './ProjectCard';
import { surfaceStatusPill, type ProjectStatus } from './project-status';
import { useSidebarCollapse } from './sidebar-collapse';
import { SidebarToggle } from './SidebarToggle';

export type TopBarProps = {
  /** Label rendered in the breadcrumb area (e.g. "EXPAND", "DASHBOARD"). */
  label?: string;
  /** Optional centered content (e.g. ProgressSteps for the upload flow). */
  center?: ReactNode;
  /**
   * Optional action(s) pinned into the top-right controls, before the profile
   * avatar — e.g. the Assembly "Download" button (FR-034).
   */
  actions?: ReactNode;
  /** Canonical lifecycle stage for in-trial surfaces. */
  stage?: ProjectStatus;
  reviewProgress?: ProjectReviewProgress;
  latestFinalisedVersion?: string;
  /** Signed-in user's display name shown in the top-right (from the ID token). */
  userName?: string;
  /** When provided, renders a "Sign out" button in the top-right (F-009 FR-022). */
  onSignOut?: () => void;
};

/** Up-to-two-letter initials for the profile avatar (design review #9). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

type StageIndicatorProps = {
  stage: ProjectStatus;
  reviewProgress?: ProjectReviewProgress;
  latestFinalisedVersion?: string;
};

function StageIndicator({ stage, reviewProgress, latestFinalisedVersion }: StageIndicatorProps) {
  const pill = surfaceStatusPill(stage);

  return (
    <div aria-label="Trial stage" className="ml-3 flex min-w-0 items-center gap-2 tracking-normal">
      <Pill tone={pill.tone} size="sm">
        {pill.label}
      </Pill>
      {stage === 'in_review' && reviewProgress ? (
        <span className="whitespace-nowrap text-sm font-medium text-text-secondary">
          {reviewProgress.ready} of {reviewProgress.total} ready
        </span>
      ) : null}
      {stage === 'finalised' && latestFinalisedVersion ? (
        <span className="whitespace-nowrap text-sm font-medium text-text-secondary">
          {latestFinalisedVersion}
        </span>
      ) : null}
    </div>
  );
}

export function TopBar({
  label = 'EXPAND',
  center,
  actions,
  stage,
  reviewProgress,
  latestFinalisedVersion,
  userName,
  onSignOut,
}: TopBarProps) {
  // F-043: the collapse toggle renders only inside the AppShell provider.
  const { isAvailable } = useSidebarCollapse();
  return (
    <header className="flex h-topbar items-center justify-between border-b border-border bg-surface-app px-8 py-4">
      <div className="flex min-w-0 items-center gap-[10px] text-sm tracking-[0.06em] text-text-secondary">
        {isAvailable && <SidebarToggle />}
        <span className="ml-[3px] text-sm font-medium uppercase leading-[20px] tracking-normal text-[#292938]">
          {label}
        </span>
        {stage ? (
          <StageIndicator
            stage={stage}
            {...(reviewProgress === undefined ? {} : { reviewProgress })}
            {...(latestFinalisedVersion === undefined ? {} : { latestFinalisedVersion })}
          />
        ) : null}
      </div>
      {center && <div className="flex flex-1 justify-center">{center}</div>}
      {/* Design review #9: top-right controls — the optional actions (e.g.
          Download) sit before the profile avatar. */}
      <div className="flex shrink-0 items-center justify-end gap-2">
        {actions}
        {userName !== undefined && (
          <button
            type="button"
            title={userName}
            aria-label={onSignOut === undefined ? userName : `${userName} — account menu`}
            onClick={onSignOut}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-pill bg-brand-primary text-xs font-semibold text-text-inverse transition-colors hover:bg-brand-primary-hover"
          >
            {initials(userName)}
          </button>
        )}
      </div>
    </header>
  );
}
